import type * as React from "react";
import type {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  RowData,
  SortingState,
} from "@tanstack/react-table";
import {
  columnFilteringFeature,
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSortingFeature,
  tableFeatures,
} from "@tanstack/react-table";

export const defaultTableFeatures = tableFeatures({
  rowSortingFeature,
  rowPaginationFeature,
  columnFilteringFeature,
  columnVisibilityFeature,
});

export type DefaultTableFeatures = typeof defaultTableFeatures;

export type DataTableColumnDef<
  TData extends RowData,
  TValue = unknown,
> = ColumnDef<DefaultTableFeatures, TData, TValue>;

export interface DataTablePaginationMeta {
  totalCount: number;
  currentPage: number;
  totalPages: number;
  firstPageUrl: string | null;
  lastPageUrl: string | null;
  nextPageUrl: string | null;
  previousPageUrl: string | null;
}

export interface DataTableLabels {
  empty: React.ReactNode;
  error: React.ReactNode;
  loading: string;
  showingCount: (count: number, total: number) => React.ReactNode;
  nextPage: string;
  previousPage: string;
  rowsPerPage: string;
  rowsPerPageLabel?: string | undefined;
  sortAscending: string;
  sortDescending: string;
  actionMenuTrigger: string;
}

export interface DataTableActionMenuLabels {
  trigger: string;
  view?: string | undefined;
  edit?: string | undefined;
  delete?: string | undefined;
  deactivate?: string | undefined;
  resendActivation?: string | undefined;
  extend?: string | undefined;
}

export interface DataTableProps<TData extends RowData, TValue = unknown> {
  data: ReadonlyArray<TData>;
  columns: ReadonlyArray<DataTableColumnDef<TData, TValue>>;
  paginationMeta?: DataTablePaginationMeta | undefined;
  sorting?: SortingState | undefined;
  onSortingChange?: OnChangeFn<SortingState> | undefined;
  page?: number | undefined;
  pageIndex?: number | undefined;
  pageSize?: number | undefined;
  pageSizeOptions?: ReadonlyArray<number> | undefined;
  onPaginationChange?: OnChangeFn<PaginationState> | undefined;
  onPageSizeChange?: ((size: number) => void) | undefined;
  isLoading?: boolean | undefined;
  isError?: boolean | undefined;
  onRowClick?: ((row: TData) => void) | undefined;
  labels: DataTableLabels;
  className?: string | undefined;
  containerClassName?: string | undefined;
  skeletonRowCount?: number | undefined;
}
